import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, User, Briefcase, Calendar, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";

// Typeable combo input — shows dropdown suggestions, accepts free text
const ComboInput = ({ value, onChange, options, placeholder, testId }) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const filtered = inputValue.trim() === ""
    ? options
    : options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase()));

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (opt) => {
    setInputValue(opt);
    onChange(opt);
    setOpen(false);
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
    setOpen(true);
  };

  const showAddOption = inputValue.trim() && !options.some(o => o.toLowerCase() === inputValue.trim().toLowerCase());

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-b-2 border-white/20 focus:border-neon-purple outline-none text-lg py-3 px-0 text-white placeholder:text-white/30 font-rajdhani"
      />
      {open && (filtered.length > 0 || showAddOption) && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md bg-[#1a1a2e] border border-white/10 max-h-52 overflow-y-auto shadow-2xl">
          {filtered.map((opt) => (
            <button
              key={opt}
              type="button"
              onMouseDown={() => handleSelect(opt)}
              className="w-full text-left px-4 py-2.5 text-white/80 hover:bg-white/10 hover:text-white font-rajdhani text-sm transition-colors"
            >
              {opt}
            </button>
          ))}
          {showAddOption && (
            <button
              type="button"
              onMouseDown={() => handleSelect(inputValue.trim())}
              className="w-full text-left px-4 py-2.5 text-neon-cyan/80 hover:bg-neon-cyan/10 hover:text-neon-cyan font-rajdhani text-sm border-t border-white/10 transition-colors"
            >
              + Add "{inputValue.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const BirthDatePicker = ({ value, onChange }) => {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(value ? value.getMonth() : today.getMonth());
  const [viewYear, setViewYear] = useState(value ? value.getFullYear() : 1990);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);

  const years = [];
  for (let y = today.getFullYear(); y >= 1940; y--) years.push(y);

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(viewMonth, viewYear);
  const firstDay = getFirstDayOfMonth(viewMonth, viewYear);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const selectDay = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    if (date > today || date < new Date("1940-01-01")) return;
    onChange(date);
  };

  const isSelected = (day) => value && value.getDate() === day && value.getMonth() === viewMonth && value.getFullYear() === viewYear;
  const isDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    return d > today || d < new Date("1940-01-01");
  };

  if (showMonthPicker) return (
    <div className="w-full">
      <button onClick={() => setShowMonthPicker(false)} className="text-neon-cyan font-rajdhani text-sm mb-3 flex items-center gap-1 hover:text-white transition-colors">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="grid grid-cols-3 gap-2">
        {MONTHS.map((m, i) => (
          <button key={m} onClick={() => { setViewMonth(i); setShowMonthPicker(false); }}
            className={`py-2 px-1 rounded text-sm font-rajdhani transition-all ${viewMonth === i ? "bg-neon-purple text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
            {m.slice(0, 3)}
          </button>
        ))}
      </div>
    </div>
  );

  if (showYearPicker) return (
    <div className="w-full">
      <button onClick={() => setShowYearPicker(false)} className="text-neon-cyan font-rajdhani text-sm mb-3 flex items-center gap-1 hover:text-white transition-colors">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
        {years.map(y => (
          <button key={y} onClick={() => { setViewYear(y); setShowYearPicker(false); }}
            className={`py-1.5 rounded text-sm font-rajdhani transition-all ${viewYear === y ? "bg-neon-purple text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
            {y}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-1 text-white/60 hover:text-white transition-colors"><ChevronLeft className="w-5 h-5" /></button>
        <div className="flex gap-2">
          <button onClick={() => setShowMonthPicker(true)} className="font-orbitron text-sm text-white hover:text-neon-cyan transition-colors tracking-wider">
            {MONTHS[viewMonth]}
          </button>
          <button onClick={() => setShowYearPicker(true)} className="font-orbitron text-sm text-neon-purple hover:text-neon-cyan transition-colors tracking-wider">
            {viewYear}
          </button>
        </div>
        <button onClick={nextMonth} className="p-1 text-white/60 hover:text-white transition-colors"><ChevronRight className="w-5 h-5" /></button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 mb-1">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
          <div key={d} className="text-center text-xs text-white/30 font-rajdhani py-1">{d}</div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-y-1">
        {Array(firstDay).fill(null).map((_, i) => <div key={`e-${i}`} />)}
        {Array(daysInMonth).fill(null).map((_, i) => {
          const day = i + 1;
          const disabled = isDisabled(day);
          const selected = isSelected(day);
          return (
            <button key={day} onClick={() => selectDay(day)} disabled={disabled}
              className={`aspect-square flex items-center justify-center rounded text-sm font-rajdhani transition-all
                ${selected ? "bg-neon-purple text-white shadow-[0_0_10px_rgba(127,90,240,0.5)]" : ""}
                ${!selected && !disabled ? "text-white/80 hover:bg-white/10 hover:text-white" : ""}
                ${disabled ? "text-white/15 cursor-not-allowed" : "cursor-pointer"}`}>
              {day}
            </button>
          );
        })}
      </div>

      {value && (
        <div className="mt-4 pt-3 border-t border-white/10 text-center text-neon-cyan font-rajdhani text-sm">
          {format(value, "MMMM d, yyyy")}
        </div>
      )}
    </div>
  );
};

const TITLES = [
  { value: "Mr.", label: "Mr. (He/Him)" },
  { value: "Mrs.", label: "Mrs. (She/Her)" },
  { value: "Miss", label: "Miss (She/Her)" },
  { value: "Ms.", label: "Ms. (She/Her)" },
  { value: "Dr.", label: "Dr." },
  { value: "Mx.", label: "Mx. (They/Them)" },
];

const VisitorForm = ({ onSubmit, onBack, savedData }) => {
  const [formStep, setFormStep] = useState(0);
  const [formData, setFormData] = useState({
    title: savedData?.title || "",
    name: savedData?.name || "",
    profession: savedData?.profession || "",
    role: savedData?.role || "",
    experience: savedData?.experience !== undefined ? String(savedData.experience) : "",
    birth_date: savedData?.birth_date ? new Date(savedData.birth_date) : null,
  });

  const professions = [
    "Software Engineering",
    "Product Management",
    "Design",
    "Data Science",
    "Marketing",
    "Sales",
    "Operations",
    "Finance",
    "HR",
    "Other"
  ];

  const roles = [
    "Individual Contributor",
    "Team Lead",
    "Manager",
    "Director",
    "VP",
    "C-Level",
    "Founder/Co-Founder",
    "Consultant",
    "Student",
    "Other"
  ];

  const handleNext = () => {
    if (formStep < 3) {
      setFormStep(formStep + 1);
    } else {
      onSubmit({
        ...formData,
        experience: parseInt(formData.experience) || 0,
        birth_date: formData.birth_date ? format(formData.birth_date, "yyyy-MM-dd") : ""
      });
    }
  };

  const handleBack = () => {
    if (formStep > 0) {
      setFormStep(formStep - 1);
    } else {
      onBack();
    }
  };

  const isStepValid = () => {
    switch (formStep) {
      case 0:
        return formData.title !== "" && formData.name.trim().length >= 2;
      case 1:
        return formData.profession && formData.role;
      case 2:
        return formData.experience !== "";
      case 3:
        return formData.birth_date !== null;
      default:
        return false;
    }
  };

  const steps = [
    {
      title: "Who are you?",
      subtitle: "Select your title and enter your name",
      icon: User
    },
    {
      title: "What do you do?",
      subtitle: "Tell us about your profession",
      icon: Briefcase
    },
    {
      title: "Your experience",
      subtitle: "How many years in your field?",
      icon: Clock
    },
    {
      title: "When were you born?",
      subtitle: "For numerology insights",
      icon: Calendar
    }
  ];

  const currentStep = steps[formStep];
  const StepIcon = currentStep.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center relative px-6"
      data-testid="visitor-form"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-[600px] h-[600px] bg-neon-purple/10 rounded-full blur-[120px]" />
      </div>

      {/* Back button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={handleBack}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-white transition-colors font-rajdhani"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </motion.button>

      {/* Progress indicator */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 flex gap-2">
        {[0, 1, 2, 3].map((step) => (
          <div
            key={step}
            className={`w-12 h-1 rounded-full transition-all duration-300 ${
              step === formStep
                ? "bg-neon-purple shadow-[0_0_10px_rgba(127,90,240,0.5)]"
                : step < formStep
                ? "bg-neon-green"
                : "bg-white/20"
            }`}
          />
        ))}
      </div>

      {/* Form content */}
      <div className="relative z-10 w-full max-w-lg mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={formStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            {/* Step icon */}
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-neon-purple/20 to-neon-cyan/20 flex items-center justify-center border border-white/10">
              <StepIcon className="w-8 h-8 text-neon-purple" />
            </div>

            {/* Step title */}
            <h2 className="font-orbitron text-2xl sm:text-3xl font-bold text-white mb-2 tracking-wider">
              {currentStep.title}
            </h2>
            <p className="font-rajdhani text-white/60 mb-8">
              {currentStep.subtitle}
            </p>

            {/* Form inputs */}
            <div className="glass p-8 rounded-lg">
              {formStep === 0 && (
                <div className="space-y-5">
                  <div>
                    <p className="font-rajdhani text-white/50 text-xs tracking-widest uppercase mb-2">Title</p>
                    <div className="flex flex-wrap gap-2">
                      {TITLES.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, title: t.value })}
                          className={`px-4 py-2 rounded-sm font-rajdhani text-sm tracking-wide transition-all ${
                            formData.title === t.value
                              ? "bg-neon-purple text-white shadow-[0_0_12px_rgba(127,90,240,0.5)]"
                              : "bg-white/5 border border-white/15 text-white/60 hover:bg-white/10 hover:text-white"
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Input
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-transparent border-b-2 border-white/20 focus:border-neon-purple rounded-none text-xl py-3 px-0 text-white placeholder:text-white/30 font-rajdhani"
                    autoFocus
                    data-testid="name-input"
                  />
                </div>
              )}

              {formStep === 1 && (
                <div className="space-y-6">
                  <ComboInput
                    value={formData.profession}
                    onChange={(value) => setFormData({ ...formData, profession: value })}
                    options={professions}
                    placeholder="Type or select your profession"
                    testId="profession-select"
                  />
                  <ComboInput
                    value={formData.role}
                    onChange={(value) => setFormData({ ...formData, role: value })}
                    options={roles}
                    placeholder="Type or select your role"
                    testId="role-select"
                  />
                </div>
              )}

              {formStep === 2 && (
                <div className="flex items-center justify-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    placeholder="0"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    className="bg-transparent border-b-2 border-white/20 focus:border-neon-purple rounded-none text-4xl py-3 px-0 text-white text-center w-24 font-orbitron"
                    data-testid="experience-input"
                  />
                  <span className="text-white/60 text-xl font-rajdhani">years</span>
                </div>
              )}

              {formStep === 3 && (
                <BirthDatePicker
                  value={formData.birth_date}
                  onChange={(date) => setFormData({ ...formData, birth_date: date })}
                />
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Next button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={handleNext}
          disabled={!isStepValid()}
          className={`mt-8 w-full py-4 font-orbitron tracking-widest uppercase rounded-sm flex items-center justify-center gap-3 transition-all duration-300 ${
            isStepValid()
              ? "bg-neon-purple text-white hover:bg-neon-purple/80 shadow-[0_0_20px_rgba(127,90,240,0.4)]"
              : "bg-white/10 text-white/30 cursor-not-allowed"
          }`}
          data-testid="next-btn"
        >
          {formStep === 3 ? "Continue to Photo" : "Next"}
          <ArrowRight className="w-5 h-5" />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default VisitorForm;
